import { MdClear } from 'react-icons/md';
import { PROFIT_STATUS_ITEMS } from '../constants';
import EntryDateRangePicker from './EntryDateRangePicker';
import useFetchActiveUsers from '@/features/agent/api/useFetchActiveUsers';
import useFetchTeams from '@/features/agent/api/useFetchTeams';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { Button } from '@/components/Button';

type SearchConditions = {
  query: string;
  status: string;
  companyOrganizationUserId: string;
  candidateOrganizationUserId: string;
  companyId: string;
  teamId: string;
  sort: string;
  entryDateFrom: string;
  entryDateTo: string;
};

type Props = {
  conditions: SearchConditions;
  onChange: (conditions: SearchConditions) => void;
};

const ALL_VALUE = 'all';

const statusItems: { value: string; label: string }[] = [
  { value: ALL_VALUE, label: '全て' },
  ...PROFIT_STATUS_ITEMS.filter((item) => item.value !== '').map((item) => ({
    value: item.value,
    label: item.label,
  })),
];

function buildAssigneeSelectItems(
  records: { id: number; name: string }[] | undefined
): { value: string; label: string }[] {
  return [
    { value: ALL_VALUE, label: '全て' },
    ...(records?.map((user) => ({ value: String(user.id), label: user.name })) ?? []),
  ];
}

export default function ProfitsSearchBox({ conditions, onChange }: Props) {
  const { data: activeUsersData } = useFetchActiveUsers();
  const assigneeItems = buildAssigneeSelectItems(activeUsersData?.records);

  const { data: teamsData } = useFetchTeams();
  const teamItems: { value: string; label: string }[] = [
    { value: ALL_VALUE, label: '全て' },
    ...(teamsData?.records?.map((team) => ({ value: String(team.id), label: team.name })) ?? []),
  ];

  const update = (key: keyof SearchConditions, value: string) => {
    onChange({ ...conditions, [key]: value });
  };

  const hasConditions =
    conditions.query ||
    conditions.status ||
    conditions.companyOrganizationUserId ||
    conditions.candidateOrganizationUserId ||
    conditions.companyId ||
    conditions.teamId ||
    conditions.entryDateFrom ||
    conditions.entryDateTo;

  const handleClear = () => {
    onChange({
      query: '',
      status: '',
      companyOrganizationUserId: '',
      candidateOrganizationUserId: '',
      companyId: '',
      teamId: '',
      sort: 'updated_at',
      entryDateFrom: '',
      entryDateTo: '',
    });
  };

  return (
    <div className="mb-6 w-full rounded bg-white p-5 shadow-layer1">
      <div className="space-y-5">
        {/* 1段目: キーワード, ステータス */}
        <div className="flex flex-wrap gap-5">
          <div className="flex w-full max-w-[426px] min-w-0 flex-col gap-2">
            <label className="text-sm font-bold">フリーワード</label>
            <Input
              search
              placeholder="候補者名・企業名・メモで検索"
              value={conditions.query}
              onChange={(e) => update('query', e.target.value)}
              className="w-full"
            />
          </div>

          <div className="flex w-[200px] shrink-0 flex-col gap-2">
            <label className="text-sm font-bold">ステータス</label>
            <Select
              items={statusItems}
              value={conditions.status === '' ? ALL_VALUE : conditions.status}
              onValueChange={(v) => update('status', v === ALL_VALUE ? '' : v)}
            />
          </div>
        </div>

        {/* 入社予定日・担当者（1段で折り返し） */}
        <div className="flex flex-wrap items-end gap-5">
          <div className="flex w-[300px] shrink-0 flex-col gap-2">
            <label className="text-sm font-bold">入社予定日</label>
            <EntryDateRangePicker
              from={conditions.entryDateFrom}
              to={conditions.entryDateTo}
              onChange={(from, to) =>
                onChange({ ...conditions, entryDateFrom: from, entryDateTo: to })
              }
            />
          </div>
          <div className="flex w-[220px] shrink-0 flex-col gap-2">
            <label className="text-sm font-bold">企業担当者</label>
            <Select
              className="w-full"
              items={assigneeItems}
              value={
                conditions.companyOrganizationUserId === ''
                  ? ALL_VALUE
                  : conditions.companyOrganizationUserId
              }
              onValueChange={(v) => update('companyOrganizationUserId', v === ALL_VALUE ? '' : v)}
            />
          </div>
          <div className="flex w-[220px] shrink-0 flex-col gap-2">
            <label className="text-sm font-bold">求職者担当者</label>
            <Select
              className="w-full"
              items={assigneeItems}
              value={
                conditions.candidateOrganizationUserId === ''
                  ? ALL_VALUE
                  : conditions.candidateOrganizationUserId
              }
              onValueChange={(v) => update('candidateOrganizationUserId', v === ALL_VALUE ? '' : v)}
            />
          </div>
          <div className="flex w-[220px] shrink-0 flex-col gap-2">
            <label className="text-sm font-bold">グループ</label>
            <Select
              className="w-full"
              items={teamItems}
              value={conditions.teamId === '' ? ALL_VALUE : conditions.teamId}
              onValueChange={(v) => update('teamId', v === ALL_VALUE ? '' : v)}
            />
          </div>
        </div>

        {/* クリアボタン */}
        {hasConditions && (
          <div className="flex justify-end">
            <Button
              color="secondary"
              size="sm"
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1"
            >
              <MdClear className="size-icon-xs" />
              <span className="text-xs font-bold">検索条件をクリア</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export type { SearchConditions };
